import { AgeValidationAlert } from "../../shared/AgeValidationAlert";
import { Alert, Box, Typography } from "@mui/material";
import { CategoryTypes } from "../../../interfaces/enums";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { useAgeValidation } from "../../../hooks/useAgeValidation";
import { ValidationAlertsProps } from "../types/PresentationFormTypes";
/**
 * ============================================================================
 * VALIDATION ALERTS - KOMPONENT ALERTÓW WALIDACJI
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Wyświetla komunikaty błędów walidacji formularza prezentacji
 * - Pokazuje informacje o już wybranych zawodnikach w kategorii
 * - Wyświetla podpowiedzi dla różnych typów kategorii (SOLO, DUO, TRIO, OTHER)
 * - Informuje o automatycznym generowaniu nazw prezentacji
 * - Waliduje wymagalność pól formularza (nazwa, tytuł)
 *
 * ARCHITEKTURA:
 * - Presentational Component: Tylko wyświetla dane, nie zarządza stanem
 * - Conditional Rendering: Pokazuje różne alerty w zależności od stanu
 * - Material-UI: Używa komponentów Alert i Box do spójnego wyglądu
 * - Color Coding: Różne kolory dla różnych typów komunikatów
 *
 * PRZEPŁYW DANYCH:
 * 1. Otrzymuje dane walidacji przez props (participants, category, formData)
 * 2. Oblicza wyniki walidacji lokalnie (liczba zawodników, pola formularza)
 * 3. Wyświetla odpowiednie alerty na podstawie wyników walidacji
 * 4. Pokazuje informacje pomocnicze dla użytkownika
 *
 * TYPY ALERTÓW:
 * - ERROR: Błędy walidacji (nieprawidłowa liczba zawodników, brak wymaganych pól)
 * - WARNING: Informacje o już wybranych zawodnikach
 * - INFO: Podpowiedzi dla różnych typów kategorii
 * - SUCCESS: Potwierdzenia (np. liczba prezentacji do utworzenia)
 */


export default function ValidationAlerts({
  participants,
  category,
  existingCount,
  formData,
}: ValidationAlertsProps) {
  // Walidacja wieku zawodników - sprawdza limit odstępstwa wiekowego
  const ageValidation = useAgeValidation(participants, category);
  // ============================================================================
  // OBLICZANIE WYNIKÓW WALIDACJI
  // ============================================================================

  // Liczba wybranych zawodników
  const participantsCount = participants.length;

  // Walidacja liczby zawodników na podstawie typu kategorii
  // Wejście: participants (lista zawodników), category (dane kategorii)
  // Wyjście: boolean (true jeśli liczba jest poprawna)
  // Reguły:
  // - SOLO: minimum 0 zawodników (pozwala na pustą listę - usunięcie wszystkich prezentacji)
  // - DUO/TRIO/OTHER: między minPresentationSize a maxPresentationSize
  const isValidParticipantsCount = category
    ? category.type === CategoryTypes.SOLO
      ? participantsCount >= 0
      : participantsCount >= category.minPresentationSize &&
        participantsCount <= category.maxPresentationSize
    : true;

  // Walidacja pól formularza (nazwa prezentacji)
  // Wejście: category (dane kategorii), formData (dane formularza)
  // Wyjście: boolean (true jeśli pola są poprawne)
  // Reguły:
  // - SOLO: nazwa nie jest wymagana (generowana automatycznie)
  // - DUO/TRIO/OTHER: nazwa jest wymagana (musi być wprowadzona)
  const isValidFormFields = category
    ? category.type === CategoryTypes.SOLO || (formData?.name || "").trim()
    : true;

  // ============================================================================
  // RENDEROWANIE ALERTÓW WALIDACJI
  // ============================================================================

  return (
    <Box sx={{ mb: 3 }}>
      {/* ========================================================================
          BŁĘDY WALIDACJI LICZBY ZAWODNIKÓW
          ======================================================================== */}

      {/* Wyświetl błąd jeśli liczba zawodników nie jest zgodna z wymaganiami kategorii */}
      {!isValidParticipantsCount && category?.type !== CategoryTypes.SOLO && (
        <Alert
          severity="error"
          sx={{ mb: 3, border: "1px solid", borderColor: "error.main", borderRadius: 1 }}
        >
          Musisz wybrać między <strong>{category?.minPresentationSize}</strong> a{" "}
          <strong>{category?.maxPresentationSize}</strong> zawodników.
        </Alert>
      )}

      {/* ========================================================================
          WALIDACJA WIEKU ZAWODNIKÓW
          ======================================================================== */}

      {/* Komponent do walidacji wieku zawodników - wyświetla błąd gdy przekroczono limit odstępstwa wiekowego */}
      <AgeValidationAlert validationResult={ageValidation} categoryName={category?.name} />

      {/* ========================================================================
          INFORMACJA O JUŻ WYBRANYCH ZAWODNIKACH
          ======================================================================== */}

      {/* Wyświetl ostrzeżenie o zawodnikach już wybranych w innych prezentacjach */}
      {existingCount > 0 && category?.type !== CategoryTypes.SOLO && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#fef3c7", // Żółte tło
            borderRadius: 1,
            border: "1px solid #fbbf24", // Żółta ramka
          }}
        >
          <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 500 }}>
            ⚠️ {existingCount} zawodników już ma prezentacje w tej kategorii i nie może być
            wybranych ponownie
          </Typography>
        </Box>
      )}

      {/* ========================================================================
          INFORMACJA O AUTOMATYCZNEJ NAZWIE DLA DUETÓW
          ======================================================================== */}

      {/* Wyświetl informację o automatycznym generowaniu nazwy dla duetów */}
      {category?.type === CategoryTypes.DUO && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#f0f9ff", // Niebieskie tło
            borderRadius: 1,
            border: "1px solid #bae6fd", // Niebieska ramka
          }}
        >
          <Typography variant="body2" sx={{ color: "#0369a1", fontWeight: 500 }}>
            ℹ️ Nazwa prezentacji zostanie automatycznie wygenerowana z imion i nazwisk wybranych
            zawodników
          </Typography>
        </Box>
      )}

      {/* ========================================================================
          INFORMACJA O PREZENTACJACH SOLO
          ======================================================================== */}

      {/* Wyświetl potwierdzenie liczby prezentacji solo do utworzenia */}
      {category?.type === CategoryTypes.SOLO && participantsCount > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "#f0fdf4", // Zielone tło
            borderRadius: 1,
            border: "1px solid #bbf7d0", // Zielona ramka
          }}
        >
          <Typography variant="body2" sx={{ color: "#10b981", fontWeight: 500 }}>
            ✓ Zostanie utworzonych {participantsCount} prezentacji solo
          </Typography>
        </Box>
      )}
    </Box>
  );
}
